import moment from 'moment';
import {
    getSupportLink,
    isActive,
    isFinished,
    isProjectADugnad,
    isProjectPartOfTVA,
    isStartingInTheFuture,
} from '../../project-model';
import ThemeContainer from '../../../components/theme-container/ThemeContainer';
import { THEMES } from '../../../utils/cms-utils';
import { useProject } from '../../project-hooks/useProject';
import { useSelector } from 'react-redux';
import { getPublishedRewardsForProject } from '../../projects-reducer';
import { useLocation, useNavigate } from 'react-router';
import { getPropertiesForProjectABCTest, trackEvent } from '../../../support-and-tracking/analytics-actions';
import { getRemainingTimeTexts } from '../../../utils';
import ShareModal from '../../share/ShareModal';
import { useEffect, useRef, useState } from 'react';
import { useMoveIntercomAway } from '../../../support-and-tracking/intercom-hooks';
import { CartSupportButtons } from './CartSupportButtons';
import { StandardSupportButtons } from './StandardSupportButtons';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { useCart } from '../../project-hooks/useCart';
import { NumberOfRecentTransactions } from '../number-of-recent-transactions/NumberOfRecentTransactions';
import { useProjectABCTest } from '../../../components/hooks/projectAbcTest';
import styles from '../project-info-box/ProjectInfoBox.module.less';
import { SUPPORT_PROJECT_LONG_TEXT_KEY } from '../../i18n/project-i18n-model';
import { useI18nTexts } from '../../i18n/useI18nTexts';

const ProjectSupportButtonsActive = ({ sharePressed, promoteActivity }) => {
    const { sum: cartSum, count: cartCount } = useCart();
    const { subproject } = useSubproject();
    const project = useProject();
    const navigate = useNavigate();
    const location = useLocation();
    const publishedRewards = useSelector((state) => getPublishedRewardsForProject(state, project.id));
    const texts = useI18nTexts([SUPPORT_PROJECT_LONG_TEXT_KEY]);

    useMoveIntercomAway();

    const navigateToPayment = () => {
        const currentPathname = location?.pathname;
        const supportLink = getSupportLink({ projectId: project.id, rewards: publishedRewards, currentPathname });
        navigate({
            pathname: supportLink,
            search: subproject ? `?subproject=${subproject.id}` : '',
        });
    };

    const navigateToCart = () => {
        navigate({
            pathname: `/project/${project.id}/payment`,
            search: subproject ? `?subproject=${subproject.id}` : '',
        });
    };

    return (
        <ThemeContainer mainColor={THEMES.NORDLYS30} className={styles.supportButtons}>
            {cartSum ? (
                <CartSupportButtons cartSum={cartSum} cartCount={cartCount} navigateToCart={navigateToCart} />
            ) : promoteActivity ? (
                <>
                    <NumberOfRecentTransactions showActivityPromotion />
                    <StandardSupportButtons
                        project={project}
                        navigateToPayment={navigateToPayment}
                        sharePressed={sharePressed}
                        wrapperClass={styles.buttonGroupWrapper}
                        showShare={false}
                        supportButtonText={texts[SUPPORT_PROJECT_LONG_TEXT_KEY]}
                    />
                </>
            ) : (
                <>
                    <NumberOfRecentTransactions />
                    <StandardSupportButtons
                        project={project}
                        navigateToPayment={navigateToPayment}
                        sharePressed={sharePressed}
                        wrapperClass={styles.buttonGroupWrapper}
                    />
                </>
            )}
        </ThemeContainer>
    );
};

const ProjectSupportButtons = ({ className, showModal = false, onCloseShareModal = () => {}, promoteActivity }) => {
    const project = useProject();

    const { generalText: statusText } = getRemainingTimeTexts(project);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const supportButtons = useRef(null);
    const productCategoryModalOpen = useSelector((state) => state.productCategoryModal.isOpen);

    const [isFixed, setIsFixed] = useState(false);
    const campaigns = useSelector((state) => state.campaigns);
    const isTvaProject = isProjectPartOfTVA(campaigns, project);
    const hideOnMobileWhenModalIsOpen = isFixed && productCategoryModalOpen;
    const abcTest = useProjectABCTest({ project });

    useEffect(() => {
        if (supportButtons?.current) {
            const isFixed = supportButtons?.current && getComputedStyle(supportButtons?.current).position === 'fixed';
            setIsFixed(isFixed);
        }
    }, [supportButtons]);

    const sharePressed = () => {
        trackEvent('project', 'share', {
            channel: 'modal',
            'project-id': project.id,
            location: 'banner',
            ...getExtraTrackingParameters(),
        });
        setShareModalOpen(true);
    };

    const getExtraTrackingParameters = () => ({
        ...getPropertiesForProjectABCTest({ variant: abcTest.variant, eligible: abcTest.eligible }),
    });

    return (
        <div
            className={[
                styles.supportButtonsWrapper,
                isActive(project) ? styles.projectIsActive : '',
                hideOnMobileWhenModalIsOpen ? styles.hideSupportButtons : '',
                className,
            ].join(' ')}
            ref={supportButtons}
        >
            {isActive(project) ? (
                <ProjectSupportButtonsActive sharePressed={sharePressed} promoteActivity={promoteActivity} />
            ) : (
                <ThemeContainer
                    mainColor={THEMES.FJELL30}
                    className={[styles.projectIsInactive, isFinished(project) ? styles.projectStatusFinished : ''].join(
                        ' '
                    )}
                >
                    <span suppressHydrationWarning>
                        {isStartingInTheFuture(project) ? (
                            <span className={styles.startingInTheFutureInfo}>
                                {isProjectADugnad(project) ? 'Dugnaden' : 'Spleisen'} blir automatisk aktiv fra og med{' '}
                                <span className={styles.startingInTheFutureInfoDate}>
                                    {moment(project.published_at).format('DD. MMMM')}
                                </span>{' '}
                                {moment(project.published_at).format('YYYY')}. Først da blir det mulig å samle inn
                                penger.&nbsp;🚀
                            </span>
                        ) : (
                            statusText
                        )}
                    </span>
                </ThemeContainer>
            )}
            <ShareModal
                open={shareModalOpen || showModal}
                showFacebook={!project.profile?.hideSharingFacebook}
                showInstagram={!project.profile?.hideSharingInstagram && project.img_url}
                showPoster={!!project.img_url}
                showTvaImage={project.img_url && isTvaProject}
                showEmbed={true}
                showTwitter={!project.profile?.hideSharingTwitter}
                onRequestClose={() => {
                    setShareModalOpen(false);
                    onCloseShareModal();
                }}
                showQR={true}
                trackingExtraProperties={{ ...getExtraTrackingParameters() }}
            />
        </div>
    );
};

export default ProjectSupportButtons;
